import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SalaIcon from './SalaIcon.jsx';

/**
 * Dock — navegação alternativa à Sidebar, estilo macOS.
 *
 * Aprovado em 07/07/2026 após simulação em HTML — esta versão porta
 * as refinações feitas na rodada final do protótipo (dock-proposta-v2.html):
 * TODAS as salas que o usuário tem acesso (a curadoria fixa de 15 foi
 * removida em 07/07/2026 — a ideia sempre foi essa: o que não coubesse
 * na largura da tela ficaria disponível rolando pro lado, não fora do
 * Dock), logo da SOLLOS avulsa à esquerda como botão de Início,
 * amplitude do "crescer" bem mais discreta que a primeira tentativa,
 * rolagem lateral pelo scroll do mouse (com inércia), e o balão de nome
 * corrigido (antes vivia dentro do item e o overflow-x:auto do Dock
 * cortava ele — agora é um elemento único, posicionado via JS).
 *
 * Preferência pessoal do usuário (ver AuthContext.navEstilo) — quem
 * prefere a Sidebar continua com ela; nada muda pra quem não trocar.
 *
 * Mobile: também flutua, ícones maiores, rolagem por toque, sem magnify
 * (não faz sentido no toque) e sem as setas (o toque já rola direto).
 */

// Mede o tamanho-base de um elemento com segurança contra o bug do
// crescimento sem limite (07/07/2026): se dataset.base ainda não existe,
// NUNCA usar getBoundingClientRect() direto — a essa altura o próprio
// magnify já pode ter deixado o elemento maior que o tamanho de repouso,
// e usar esse valor "ao vivo" como base faz cada tick de mousemove
// multiplicar cima do resultado do tick anterior (bola de neve). Por
// isso: zera o estilo inline primeiro, mede o CSS puro, e só então guarda.
function medirBaseSeguro(el) {
  if (el.dataset.base !== undefined) return parseFloat(el.dataset.base);
  el.style.width = '';
  el.style.height = '';
  const largura = el.getBoundingClientRect().width;
  el.dataset.base = largura;
  return largura;
}

export default function Dock({ usuario, categorias, baseRoute = '', renderIcon }) {
  const navigate = useNavigate();
  const location = useLocation();
  const itemRefsDesktop = useRef({});
  const dockRef = useRef(null);
  const listaRefDesktop = useRef(null);
  const listaRefMobile = useRef(null);
  const tooltipRef = useRef(null);
  const scrollAlvoRef = useRef(null);
  const scrollAnimRef = useRef(null);
  const [listaAberta, setListaAberta] = useState(false);
  const [posPopup, setPosPopup] = useState(null);

  // Generalização (10/08/2026, pedido do Juan: "espelhar" Dock nos
  // demais portais). Antes o Dock só sabia navegar pra /dashboard e
  // /sala/:numero, e só sabia desenhar ícone via SalaIcon (mapa por
  // número — mapa que é ESPECÍFICO do SGQ). Isso quebraria em outro
  // portal: a Sala 9 do RH ("Colaboradores") mostraria o ícone da
  // Sala 9 do SGQ ("Treinamentos") — mesmo número, coisas diferentes.
  // baseRoute (default '' preserva o comportamento antigo do SGQ sem
  // mudar a chamada em Dashboard.jsx) prefixa as rotas; renderIcon
  // (default cai pro SalaIcon de sempre) deixa cada portal desenhar
  // com seu próprio mapa de ícones.
  const homeRota = `${baseRoute}/dashboard`;
  const rotaSala = (numero) => `${baseRoute}/sala/${numero}`;
  const icone = (sala, size) => renderIcon ? renderIcon(sala, size) : <SalaIcon numero={sala.sala_numero} size={size} />;


  // Todas as salas que esse usuário de fato tem acesso — sem teto fixo.
  // O que não couber na largura da tela fica disponível rolando pro
  // lado (scroll do mouse ou toque). As setas de deslizar existiram e
  // foram removidas (07/07/2026): elas e o Dock eram irmãos no mesmo
  // flex container, então cada vez que uma seta aparecia/desaparecia
  // no limite do scroll, o espaço disponível pro Dock mudava na hora —
  // exatamente o "tranco" que a pessoa via ao chegar no fim da lista.
  // Sem as setas, o dock tem largura estável sempre; o scroll (mouse ou
  // toque) continua sendo o único jeito de navegar, sem esse full-stop.
  const salasDock = categorias.flatMap(c => c.salas);
  // Chaves dos itens especiais (Lista/Início), que agora vivem na mesma
  // fileira das salas — precisam entrar no mesmo processamento de
  // magnify que as demais, senão ficam "paradas" no meio do efeito dos
  // vizinhos (16/07/2026, achado pelo Juan).
  const CHAVES_ESPECIAIS = ['especial-lista', 'especial-inicio'];
  const todasAsChaves = () => [...CHAVES_ESPECIAIS, ...salasDock.map(s => s.sala_numero)];

  const handleMouseMove = (e) => {
    const mouseX = e.clientX;
    todasAsChaves().forEach(chave => {
      const el = itemRefsDesktop.current[chave];
      if (!el) return;
      const icone = el.querySelector('.dock-icone');
      const svg = el.querySelector('svg');
      const r = el.getBoundingClientRect();
      const centro = r.left + r.width / 2;
      const dist = Math.abs(mouseX - centro);
      const raio = 95;
      const influencia = Math.max(0, 1 - dist / raio);
      // Amplitude bem mais discreta que a primeira tentativa (era 0.65
      // de escala e -8px) — pedido do Juan, no mesmo espírito da
      // suavização que já fizemos nos cards da Home.
      const escala = 1 + influencia * 0.34;
      if (icone) {
        const base = medirBaseSeguro(icone);
        icone.style.width = (base * escala) + 'px';
        icone.style.height = (base * escala) + 'px';
      }
      if (svg) {
        const baseSvg = medirBaseSeguro(svg);
        svg.style.width = (baseSvg * (1 + influencia * 0.2)) + 'px';
        svg.style.height = (baseSvg * (1 + influencia * 0.2)) + 'px';
      }
      el.style.transform = `translateY(${-influencia * 5}px)`;
    });
  };

  const handleMouseLeave = () => {
    todasAsChaves().forEach(chave => {
      const el = itemRefsDesktop.current[chave];
      if (!el) return;
      const icone = el.querySelector('.dock-icone');
      const svg = el.querySelector('svg');
      if (icone) { icone.style.width = ''; icone.style.height = ''; }
      if (svg) { svg.style.width = ''; svg.style.height = ''; }
      el.style.transform = 'translateY(0)';
    });
  };

  // Guarda o tamanho-base real (responsivo, via clamp no CSS) de cada
  // ícone antes de qualquer influência do magnify, senão a conta de
  // "crescer" perde a referência depois da primeira interação. Reaplicada
  // também no resize (07/07/2026): o clamp() do CSS é em vw, então o
  // tamanho de repouso muda com a largura da janela — sem recapturar
  // aqui, o magnify passaria a usar um base desatualizado depois de
  // redimensionar.
  const recapturarBases = useCallback(() => {
    Object.values(itemRefsDesktop.current).forEach(el => {
      if (!el) return;
      const icone = el.querySelector('.dock-icone');
      const svg = el.querySelector('svg');
      if (icone) { icone.style.width = ''; icone.style.height = ''; icone.dataset.base = icone.getBoundingClientRect().width; }
      if (svg) { svg.style.width = ''; svg.style.height = ''; svg.dataset.base = svg.getBoundingClientRect().width; }
    });
  }, []);

  useEffect(() => { recapturarBases(); }, [recapturarBases, salasDock.length]);
  useEffect(() => {
    window.addEventListener('resize', recapturarBases);
    return () => window.removeEventListener('resize', recapturarBases);
  }, [recapturarBases]);

  // Altura do bloco esquerdo (botão de lista em cima + logo embaixo) —
  // removido o ajuste via JS em 08/07/2026: mexer no style.height por
  // JS repetidamente (ResizeObserver a cada resize/troca de sala)
  // disparava um bug conhecido do Chrome/Android onde backdrop-filter
  // "some" (fica transparente) até algo forçar um repaint, como um
  // toque. O flexbox sozinho (align-items:stretch, herdado do wrap,
  // já que .dock-esq não tem mais align-self:flex-end) já iguala a
  // altura dos dois lado a lado, sem precisar medir nada em JS.

  // Scroll do mouse com inércia (07/07/2026) — antes o scrollLeft saltava
  // direto pro valor novo a cada tick da roda, sem transição (parecia
  // "seco"/travado). Agora acumula um alvo e desliza até lá suavemente,
  // no mesmo espírito das outras curvas do Dock (magnify, translateY).
  const animarScroll = useCallback(() => {
    const d = dockRef.current;
    if (!d || scrollAlvoRef.current === null) { scrollAnimRef.current = null; return; }
    const atual = d.scrollLeft;
    const alvo = scrollAlvoRef.current;
    const diff = alvo - atual;
    if (Math.abs(diff) < 0.5) {
      d.scrollLeft = alvo;
      scrollAlvoRef.current = null;
      scrollAnimRef.current = null;
      return;
    }
    d.scrollLeft = atual + diff * 0.2;
    scrollAnimRef.current = requestAnimationFrame(animarScroll);
  }, []);

  const handleWheel = useCallback((e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    e.preventDefault();
    const d = dockRef.current;
    if (!d) return;
    const max = d.scrollWidth - d.clientWidth;
    const base = scrollAlvoRef.current !== null ? scrollAlvoRef.current : d.scrollLeft;
    scrollAlvoRef.current = Math.min(max, Math.max(0, base + e.deltaY));
    if (!scrollAnimRef.current) scrollAnimRef.current = requestAnimationFrame(animarScroll);
  }, [animarScroll]);

  // Listener nativo, não-passivo (07/07/2026) — o onWheel do JSX é
  // registrado pelo React como passivo por padrão (pensando em
  // performance de scroll), e listener passivo IGNORA silenciosamente
  // qualquer preventDefault(): o Dock deslizava pro lado (isso é JS
  // puro, sempre funcionou), mas a página por baixo continuava rolando
  // pra cima/baixo, porque o navegador nunca era de fato impedido de
  // fazer o scroll padrão. Só um listener anexado manualmente com
  // { passive: false } consegue bloquear isso de verdade.
  useEffect(() => {
    const d = dockRef.current;
    if (!d) return;
    d.addEventListener('wheel', handleWheel, { passive: false });
    return () => d.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  useEffect(() => () => { if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current); }, []);

  // Fecha a lista de salas ao clicar fora do bloco esquerdo (que contém
  // o botão e o pop-up).
  useEffect(() => {
    if (!listaAberta) return;
    const handler = (e) => {
      const dentroDesktop = listaRefDesktop.current?.contains(e.target);
      const dentroMobile  = listaRefMobile.current?.contains(e.target);
      if (!dentroDesktop && !dentroMobile) setListaAberta(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [listaAberta]);

  const mostrarTooltip = (texto, alvoEl) => {
    const t = tooltipRef.current;
    if (!t || !alvoEl) return;
    const r = alvoEl.getBoundingClientRect();
    t.textContent = texto;
    t.style.left = (r.left + r.width / 2) + 'px';
    t.style.top = (r.top - 11) + 'px';
    t.classList.add('aberto');
  };
  const esconderTooltip = () => tooltipRef.current?.classList.remove('aberto');

  const irParaSala = (n) => { setListaAberta(false); navigate(rotaSala(n)); };

  // Lista pop-up de todas as salas, em coluna, agrupada por categoria —
  // mesma fonte (salasDock) e mesma ordem do Dock em linha.
  //
  // Renderizada como elemento ÚNICO fora da fileira rolável, posicionada
  // via JS (position:fixed + coordenadas calculadas no clique) — MESMO
  // padrão já usado no balão de nome flutuante (ver comentário no topo
  // do arquivo). Motivo: agora que o botão "Lista de Salas" vive DENTRO
  // de .dock (que tem overflow-x:auto, e por regra do próprio CSS isso
  // força overflow-y a virar auto também), um popup posicionado como
  // filho normal do botão ficava cortado quase todo — só uma fresta
  // aparecia (16/07/2026, achado pelo Juan: só 2 salas apareciam de 36).
  // A diferença pro balão de nome é de tamanho: o balão é pequeno e
  // cabia na folga de 50px que reservei pro ícone crescer; a lista
  // inteira (até 460px de altura) é grande demais pra qualquer folga
  // razoável — por isso preciso tirar ela de dentro do .dock de vez,
  // não só dar mais espaço.
  const ListaSalas = ({ pos }) => {
    const grupos = [];
    let catAtual = null;
    salasDock.forEach(s => {
      if (s.categoria !== catAtual) { grupos.push({ categoria: s.categoria, salas: [] }); catAtual = s.categoria; }
      grupos[grupos.length - 1].salas.push(s);
    });
    return (
      <div className="dock-lista-pop" style={pos ? { position: 'fixed', left: pos.left, bottom: pos.bottom, top: 'auto' } : undefined}>
        <div className="dock-lista-titulo">Todas as salas</div>
        <div className="dock-lista-scroll">
          {grupos.map((g, i) => (
            <div key={i} className="dock-lista-grupo">
              {g.categoria && <div className="dock-lista-cat">{g.categoria}</div>}
              {g.salas.map(s => {
                const ativo = location.pathname === rotaSala(s.sala_numero);
                return (
                  <button key={s.sala_numero} className={`dock-lista-item ${ativo ? 'on' : ''}`} onClick={() => irParaSala(s.sala_numero)}>
                    <span className="dock-lista-ic">{icone(s, 16)}</span>
                    <span className="dock-lista-nome">{s.nome}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Antes vivia num bloco fixo separado (dock-esq), sempre visível,
  // fora da fileira rolável — foi assim desde 07/07/2026. A pedido do
  // Juan (16/07/2026), agora entram como os dois primeiros itens da
  // PRÓPRIA fileira, no mesmo tamanho/estilo dos ícones de sala, e por
  // isso rolam junto com o resto (igual à barra lateral, onde o link
  const ItemEspecial = ({ tipo, mobile }) => {
    const ehLista = tipo === 'lista';
    const ativo = ehLista ? listaAberta : location.pathname === homeRota;
    const registrarRef = (el) => {
      if (ehLista) { if (mobile) listaRefMobile.current = el; else listaRefDesktop.current = el; }
      if (!mobile) itemRefsDesktop.current[ehLista ? 'especial-lista' : 'especial-inicio'] = el;
    };
    const aoClicar = (e) => {
      if (!ehLista) { navigate(homeRota); return; }
      if (!listaAberta) {
        // Calcula a posição ANTES de abrir — vira coordenadas fixas de
        // tela (position:fixed), não relativas ao item (que está dentro
        // do .dock, cujo recorte vertical cortaria o popup — ver
        // comentário em cima de ListaSalas). left alinhado ao botão;
        // bottom = distância do topo do botão até o rodapé da tela +
        // um respiro de 8px, pra abrir "colado" logo acima dele.
        const r = e.currentTarget.getBoundingClientRect();
        setPosPopup({ left: Math.round(r.left), bottom: Math.round(window.innerHeight - r.top + 8) });
      }
      setListaAberta(v => !v);
    };
    return (
      <div
        ref={registrarRef}
        style={{ position: 'relative' }}
        className={`dock-item ${ativo ? 'on' : ''}`}
        onClick={aoClicar}
        onMouseEnter={mobile ? undefined : (e) => mostrarTooltip(ehLista ? 'Lista de Salas' : 'Início', e.currentTarget.querySelector('.dock-icone'))}
        onMouseLeave={mobile ? undefined : esconderTooltip}
      >
        <div className="dock-icone">
          {ehLista ? (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.41" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8.25" y1="6" x2="19.5" y2="6" /><line x1="8.25" y1="12" x2="19.5" y2="12" /><line x1="8.25" y1="18" x2="19.5" y2="18" />
              <circle cx="4.5" cy="6" r="1.5" fill="currentColor" stroke="none" /><circle cx="4.5" cy="12" r="1.5" fill="currentColor" stroke="none" /><circle cx="4.5" cy="18" r="1.5" fill="currentColor" stroke="none" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.33">
              <path d="M3 11.5 12 4l9 7.5" /><path d="M5.5 10v9a1 1 0 0 0 1 1H9a1 1 0 0 0 1-1v-4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v4a1 1 0 0 0 1 1h2.5a1 1 0 0 0 1-1v-9" />
            </svg>
          )}
        </div>
        <div className="dock-dot"></div>
        {mobile && <div className="dock-legenda">{ehLista ? 'Salas' : 'Início'}</div>}
      </div>
    );
  };

  const ItemDock = ({ s, mobile }) => {
    const ativo = location.pathname === rotaSala(s.sala_numero);
    return (
      <div
        key={s.sala_numero}
        ref={mobile ? undefined : (el => { itemRefsDesktop.current[s.sala_numero] = el; })}
        className={`dock-item ${ativo ? 'on' : ''}`}
        onClick={() => navigate(rotaSala(s.sala_numero))}
        onMouseEnter={mobile ? undefined : (e) => mostrarTooltip(s.nome, e.currentTarget.querySelector('.dock-icone'))}
        onMouseLeave={mobile ? undefined : esconderTooltip}
      >
        <div className="dock-icone">{icone(s, mobile ? 21 : 22)}</div>
        <div className="dock-dot"></div>
        {mobile && <div className="dock-legenda">{s.nome}</div>}
      </div>
    );
  };

  // Separadores por categoria — mesmo tratamento visual do protótipo.
  // Os dois primeiros itens (Lista/Início) entram antes de tudo, com
  // um separador próprio antes da primeira categoria de sala de verdade.
  const itensComSeparador = (mobile) => {
    const els = [
      <ItemEspecial key="especial-lista" tipo="lista" mobile={mobile} />,
      <ItemEspecial key="especial-inicio" tipo="inicio" mobile={mobile} />,
      <div key="sep-inicial" className="dock-sep" />,
    ];
    let catAnterior = null;
    salasDock.forEach(s => {
      if (catAnterior && s.categoria !== catAnterior) {
        els.push(<div key={`sep-${s.sala_numero}`} className="dock-sep" />);
      }
      els.push(<ItemDock key={s.sala_numero} s={s} mobile={mobile} />);
      catAnterior = s.categoria;
    });
    return els;
  };

  return (
    <>
      <div className="tooltip-flutuante" ref={tooltipRef}></div>
      {listaAberta && <ListaSalas pos={posPopup} />}

      {/* Desktop — bloco esquerdo fixo (lista + logo) e salas em linha.
          .dock-visual carrega o visual da pílula (fundo, borda, altura
          curta) com overflow:visible; .dock (por dentro) é quem rola na
          horizontal e por isso É OBRIGADO pelo CSS a recortar também na
          vertical (não dá pra ter overflow-x:auto e overflow-y:visible
          ao mesmo tempo no MESMO elemento — regra do próprio CSS). Sem
          essa separação, o ícone ampliado no hover ficava "por baixo"
          da pílula em vez de vazar pra cima dela (16/07/2026, a pedido
          do Juan). .dock por dentro é mais alto (espaço de sobra pro
          ícone crescer) e sem fundo/borda próprios — só a visual de
          fora aparece pro usuário. */}
      <div className="dock-wrap-desktop">
        <div className="dock-visual">
          <div className="dock" ref={dockRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
            {itensComSeparador(false)}
          </div>
        </div>
      </div>

      {/* Mobile — mesma fileira rolável por toque, sem magnify */}
      <div className="dock-mobile-wrap">
        <div className="dock-mobile">
          {itensComSeparador(true)}
        </div>
      </div>
    </>
  );
}
